import { get as getSpaceAPI, type SpaceAPIResponse } from "$lib/spaceapi.ts";
import { html } from "html";

const template = html`
  <blockquote class="notecard space-status">
    <p><strong>space status:</strong></p>
    <ul style="list-style: none; padding: 0">
      <li class="space-status-open"></li>
      <li class="space-status-climate"></li>
    </ul>
  </blockquote>
`;

export class SpaceStatusElement extends HTMLElement {
  static observedAttributes = ["data-spaceapi"];

  #spaceapi: SpaceAPIResponse | null = null;

  #quoteElem: HTMLQuoteElement | null = null;
  #stateElem: HTMLElement | null = null;
  #climateElem: HTMLElement | null = null;

  constructor() {
    super();
  }

  attributeChangedCallback(name: string, _old: string, _new: string) {
    switch (name) {
      case "data-spaceapi":
        this.#refresh();
        break;
    }
  }

  connectedCallback() {
    // Reset element to template.
    this.innerHTML = template;

    this.#quoteElem = this.#el("blockquote") as HTMLQuoteElement;
    this.#stateElem = this.#el(".space-status-open") as HTMLElement;
    this.#climateElem = this.#el(".space-status-climate") as HTMLElement;

    this.#refresh();
  }

  disconnectedCallback() {
    this.#stateElem = null;
    this.#climateElem = null;
  }

  async #refresh() {
    const loading = document.createElement("p");
    loading.classList.add("loading");
    loading.classList.add("empty");
    loading.textContent = "loading...";

    try {
      this.#quoteElem?.appendChild(loading);
      this.#spaceapi = await getSpaceAPI(this.dataset.spaceapi);
      this.#render();
      this.#quoteElem?.removeChild(loading);
    } catch (_) {
      loading.textContent = "error!";
    }
  }

  #render() {
    if (!this.#stateElem || !this.#climateElem) {
      return;
    }

    const data = this.#spaceapi;

    if (data?.state) {
      const sinceDate = new Date(data.state.lastchange * 1000);
      const sinceISO = sinceDate.toISOString();
      const sinceLocal = sinceDate.toLocaleTimeString();
      const sinceString = formatRelativeTime(sinceDate);
      const stateString = data.state.open ? "open" : "closed";

      this.#stateElem.innerHTML = html`
        <b class="open-state">${stateString}</b> as of
        <time datetime="${sinceISO}" title="${sinceLocal}">${sinceString}</time>
      `;
    }

    if (data?.sensors) {
      const climates = [];
      if (data.sensors.temperature) {
        climates.push(
          `temperature: ${formatSensors(data.sensors.temperature)}`,
        );
      }
      if (data.sensors.humidity) {
        climates.push(`humidity: ${formatSensors(data.sensors.humidity)}`);
      }

      this.#climateElem.textContent = climates.join(", ");
    }
  }

  #el(elem: string): HTMLElement {
    return this.querySelector(elem) as HTMLElement;
  }
}

function formatSensors(sensor: { value: number; unit?: string }[]): string {
  const avg = averages(sensor);
  const unit = sensor.length > 0 ? (sensor[0].unit || "") : "";
  return `${avg.toFixed(1)}${unit}`;
}

function averages(values: Array<{ value: number }>): number {
  const sum = values.reduce((acc, curr) => acc + curr.value, 0);
  return sum / values.length;
}

const relTimeMarks = {
  days: 60 * 60 * 24,
  hours: 60 * 60,
  minutes: 60,
};

function formatRelativeTime(date: Date): string {
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const sec = (date.getTime() - Date.now()) / 1000;
  for (const [unit, mark] of Object.entries(relTimeMarks)) {
    if (Math.abs(sec) >= mark) {
      return rtf.format(
        Math.round(sec / mark),
        unit as Intl.RelativeTimeFormatUnit,
      );
    }
  }
  return rtf.format(sec, "seconds");
}

customElements.define("dma-space-status", SpaceStatusElement);
